import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

// Get API base URL from environment or default
const getApiBaseUrl = () => {
  // Check for portal-specific API URL first
  if (typeof window !== 'undefined') {
    const portalType = window.location.hostname.split('.')[0];
    const envVar = `VITE_${portalType.toUpperCase()}_API_URL`;
    if (import.meta.env[envVar]) {
      return import.meta.env[envVar];
    }
  }

  // Fallback to general API URL
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and portal info
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add portal type header if available
    if (typeof window !== 'undefined') {
      const portalType = window.location.hostname.split('.')[0];
      if (portalType && portalType !== 'localhost') {
        config.headers['X-Portal-Type'] = portalType;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      Cookies.remove('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only show toast if not already on login page
      if (!window.location.pathname.includes('/login')) {
        toast.error('Session expired. Please login again.');
        // Construct proper login path based on environment
        const basePath = import.meta.env.MODE === 'development' ? '' : '';
        const loginPath = basePath + '/login';
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You do not have permission to perform this action.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.');
    } else {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh'),
  validateEoxegenUser: (eoxegenId) => api.post('/auth/validate-eoxegen', { eoxegenId }),
};

// Users API
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  syncUsers: () => api.post('/users/sync'),
  updateUserRoles: (id, roles) => api.put(`/users/${id}/roles`, { roles }),
  getUsersByType: (userType) => api.get(`/users/type/${userType}`),
};

// Custom Users API (New Collection)
export const customUsersAPI = {
  getCustomUsers: (params) => api.get('/custom-users', { params }),
  getCustomUser: (id) => api.get(`/custom-users/${id}`),
  createCustomUser: (userData) => api.post('/custom-users', userData),
  updateCustomUser: (id, userData) => api.put(`/custom-users/${id}`, userData),
  deleteCustomUser: (id) => api.delete(`/custom-users/${id}`),
  getCustomUsersByType: (userType) => api.get(`/custom-users/type/${userType}`),
  bulkAssignRoles: (data) => api.post('/custom-users/bulk-assign-roles', data)
};

// eOxegen Authentication API
export const eoxegenAuthAPI = {
  getPermissionsByEoxegenId: (eoxegenId) => api.get(`/eoxegen-auth/permissions/${eoxegenId}`),
  checkUserPermissions: (eoxegenId, requiredPermissions = []) =>
    api.post('/eoxegen-auth/check-permissions', { eoxegenId, requiredPermissions }),
  testPermissions: (eoxegenId) => api.get(`/eoxegen-auth/test/${eoxegenId}`),
  testMultiplePermissions: (eoxegenId, requiredPermissions) =>
    api.post('/eoxegen-auth/test-multiple-permissions', { eoxegenId, requiredPermissions })
};

// Roles API
export const rolesAPI = {
  getRoles: () => api.get('/roles'),
  getRole: (id) => api.get(`/roles/${id}`),
  createRole: (roleData) => api.post('/roles', roleData),
  updateRole: (id, roleData) => api.put(`/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/roles/${id}`),
  getRolesByUserType: (userType) => api.get(`/roles/user-type/${userType}`),
  cloneRole: (id, name) => api.post(`/roles/${id}/clone`, { name }),

  // Hierarchical Permissions API
  getHierarchicalPermissions: (id) => api.get(`/roles/${id}/hierarchical-permissions`),
  updateHierarchicalPermissions: (id, permissions) => api.put(`/roles/${id}/hierarchical-permissions`, { permissions }),
  createHierarchicalPermission: (id, pageKey, pageData) => api.post(`/roles/${id}/hierarchical-permissions/${pageKey}`, pageData),
  deleteHierarchicalPermission: (id, pageKey) => api.delete(`/roles/${id}/hierarchical-permissions/${pageKey}`),
};

// Portals API
export const portalsAPI = {
  // CRUD operations
  getAllPortals: (params) => api.get('/portals', { params }),
  getPortalById: (id) => api.get(`/portals/${id}`),
  createPortal: (data) => api.post('/portals', data),
  updatePortal: (id, data) => api.put(`/portals/${id}`, data),
  deletePortal: (id) => api.delete(`/portals/${id}`),

  // Legacy portal configuration endpoints
  getPortalConfig: (portalType) => api.get(`/portals/${portalType}/config`),
  getPortalMenu: (portalType) => api.get(`/portals/${portalType}/menu`),
  getPortalPermissions: (portalType) => api.get(`/portals/${portalType}/permissions`),
  updatePortalConfig: (portalType, config) => api.put(`/portals/${portalType}/config`, config),
};

// Permissions API
export const permissionsAPI = {
  // Permission modules
  getPermissions: (params = {}) => api.get('/permissions', { params }),
  getPermission: (id) => api.get(`/permissions/${id}`),
  createPermission: (permissionData) => api.post('/permissions', permissionData),
  updatePermission: (id, permissionData) => api.put(`/permissions/${id}`, permissionData),
  deletePermission: (id) => api.delete(`/permissions/${id}`),

  // Features
  addFeature: (permissionId, featureData) => api.post(`/permissions/${permissionId}/features`, featureData),
  updateFeature: (permissionId, featureId, featureData) => api.put(`/permissions/${permissionId}/features/${featureId}`, featureData),
  deleteFeature: (permissionId, featureId) => api.delete(`/permissions/${permissionId}/features/${featureId}`),

  // Sub-features
  addSubFeature: (permissionId, featureId, subFeatureData) => api.post(`/permissions/${permissionId}/features/${featureId}/subfeatures`, subFeatureData),
  updateSubFeature: (permissionId, featureId, subFeatureId, subFeatureData) => api.put(`/permissions/${permissionId}/features/${featureId}/subfeatures/${subFeatureId}`, subFeatureData),
  deleteSubFeature: (permissionId, featureId, subFeatureId) => api.delete(`/permissions/${permissionId}/features/${featureId}/subfeatures/${subFeatureId}`),

  // Sections
  addSection: (permissionId, featureId, subFeatureId, sectionData) => api.post(`/permissions/${permissionId}/features/${featureId}/subfeatures/${subFeatureId}/sections`, sectionData),
  updateSection: (permissionId, featureId, subFeatureId, sectionId, sectionData) => api.put(`/permissions/${permissionId}/features/${featureId}/subfeatures/${subFeatureId}/sections/${sectionId}`, sectionData),
  deleteSection: (permissionId, featureId, subFeatureId, sectionId) => api.delete(`/permissions/${permissionId}/features/${featureId}/subfeatures/${subFeatureId}/sections/${sectionId}`)
};

// Admin API
export const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard'),
  getSystemHealth: () => api.get('/admin/health'),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  exportData: (type) => api.get(`/admin/export/${type}`),
  importData: (type, data) => api.post(`/admin/import/${type}`, data),
  getSystemConfig: () => api.get('/admin/config'),
  updateSystemConfig: (config) => api.put('/admin/config', config),
};

export default api;
